import { useState, useEffect } from 'react';
import { getFile, uploadFile, deleteFile } from 'Api/Firebase';
import { AUDIO, VIDEO, S3_LESSONS } from 'Constants/General';
import { s3 } from 'Services/AWS/s3';

const useLessonVideo = ({ addLesson, module, updateLesson, lesson, deleteLesson, updateLessonData }) => {
  // Variable de progreso cuando se sube un archivo de tipo video o audio.
  const [progress, setProgress] = useState(0);

  // Variable de cargando cuando se sube un archivo de tipo video o audio.
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(!lesson?.name ? '' : lesson.name);
  const [urlVideo, setUrlVideo] = useState(null);
  const [isDraft, setIsDraft] = useState(lesson?.isDraft !== undefined ? lesson.isDraft : true);
  const [isPrivate, setIsPrivate] = useState(lesson?.isPrivate !== undefined ? lesson.isPrivate : false);
  const [privateUsers, setPrivateUsers] = useState([]);

  useEffect(() => {
    let isMounted = true;

    if (!lesson?.file)
      return () => {
        isMounted = false;
        setUrlVideo(null);
      };

    if (isMounted) {
      // El backend coordinó forzar el Content-Type solo para video.
      const contentType = lesson?.LessonType?.name === VIDEO ? 'video/mp4' : undefined;
      s3.getSignedUrl(lesson?.file, contentType).then(data => {
        setUrlVideo(data?.signedUrl ?? '');
      });
    }

    return () => {
      isMounted = false;
      setUrlVideo(null);
    };
  }, []);

  const uploadLessonToFirebase = async (url, file) => uploadFile(url, file, setProgress);
  const onSave = async (file, type = VIDEO) => {
    const lessonObject = {};

    lessonObject.moduleId = module.id;
    lessonObject.name = name;
    lessonObject.file = 'src/fake';
    lessonObject.duration = 0;
    lessonObject.isDraft = isDraft;
    lessonObject.lessonType = type;
    lessonObject.privateUsers = privateUsers.map(user => user.id);

    const { newLesson, updatedModules } = await addLesson(lessonObject);
    const { id } = newLesson;

    const key = `${S3_LESSONS}/${module.id}/${id}`;

    setLoading(true);
    // Mientras se esta subiendo el archivo a S3 se muestra un cargando.
    // Además se carga el `thumbnail` del video.
    const contentType = file.type || 'video/mp4';
    s3.uploadFileToBucketWithKeyProvided(key, file, contentType).then(async () => {
      // Se restablecen los usuarios privados
      setPrivateUsers([]);
      setLoading(false);
      // Una vez confirmado la subida del archivo, subimos la lección a la base de datos.
      updateLesson({ id, file: key }, updatedModules);
    });
  };

  const onUpdate = async ({ id, file, isNewDraft, newName, isNewPrivate }) => {
    await updateLessonData({
      id,
      file,
      isDraft: isNewDraft,
      name: newName,
      isPrivate: isNewPrivate,
      privateUsers: privateUsers.map(user => user.id),
    });
  };

  // Método para borrar una lección al presionar el botón de borrar.
  const onDelete = async () => {
    // Método para verificar si se borra la lección de la lista y tambien de la base de datos.
    await deleteLesson(lesson?.id);

    // Si no hay ID quiere decir que no se guardo el archivo en S3.
    // No se hace nada.
    if (!lesson?.id) return;

    // Se verifica si la lección contiene un file de tipo "video" ó "audio" y lo borra del S3.
    if ([VIDEO, AUDIO].includes(lesson.LessonType.name)) {
      if (!lesson?.file) return;

      // Borrar de S3 si el archivo existe.
      await s3.deleteObject(lesson.file);
    }
  };

  return {
    name,
    isDraft,
    progress,
    loading,
    privateUsers,
    isPrivate,
    setPrivateUsers,
    setName,
    setIsDraft,
    setIsPrivate,
    uploadLessonToFirebase,
    onSave,
    onDelete,
    onUpdate,
    urlVideo,
  };
};

export default useLessonVideo;
